import { useEffect, useRef } from "react";
import { useGameSounds } from "@educaplay/core";
import {
  RELACIONAR_COLUMNAS_SOUNDS,
  TYPE_OF_COLUMN,
} from "../../utils/constants";
import styles from "./WrappingCard.module.scss";
import clsx from "clsx";
import { motion } from "framer-motion";

const cardVariants = {
  hidden: { opacity: 0, scale: 0.8, y: 20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.8,
    y: -20,
    transition: {
      duration: 0.2,
    },
  },
};

export function WrappingCard({
  children,
  handleClick,
  checked,
  disabled,
  position,
  circleRef,
  id,
  wrong,
  limLives,
}) {
  const sounds = useGameSounds();
  const buttonRef = useRef(null);
  const pointerStartRef = useRef(null);

  // Guardar metadatos en el nodo sin exponer el ID en atributos DOM legibles
  useEffect(() => {
    if (buttonRef.current) {
      buttonRef.current.__cardMeta = {
        id,
        side: position,
        solved: disabled,
      };
    }
  }, [id, position, disabled]);

  const handlePointerDown = (e) => {
    // Guardar la posición inicial del pointer
    pointerStartRef.current = { x: e.clientX, y: e.clientY };
    
    // Si el drag es sobre el círculo, capturar el pointer para mejor tracking en mobile
    if (e.target.closest("[data-circle]")) {
      try {
        buttonRef.current?.setPointerCapture(e.pointerId);
      } catch (error) {
        // Ignorar errores si setPointerCapture no está disponible
      }
    }
  };

  const handleClickWithDragDetection = (e) => {
    // Si no tenemos posición de inicio, el click no proviene de pointerDown válido
    if (!pointerStartRef.current) {
      if (!disabled) handleClick(id, !checked);
      return;
    }

    // Detectar si hubo un drag significativo (más de 5px)
    const distance = Math.sqrt(
      Math.pow(e.clientX - pointerStartRef.current.x, 2) +
        Math.pow(e.clientY - pointerStartRef.current.y, 2)
    );

    pointerStartRef.current = null;

    // Si la distancia es pequeña, fue un click, no un drag
    if (distance < 5) {
      if (!disabled) handleClick(id, !checked);
    }
  };
  
  return (
    <motion.button
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      ref={buttonRef}
      className={clsx(
        styles.card,
        position === TYPE_OF_COLUMN.LEFT
          ? styles.card__left
          : styles.card__right,
        checked ? styles[`card--active`] : styles[`card--inactive`],
        disabled && styles[`locked`],
        wrong && limLives && styles.wrong
      )}
      onPointerDown={handlePointerDown}
      onClick={handleClickWithDragDetection}
      onMouseEnter={() => {
        sounds.play(RELACIONAR_COLUMNAS_SOUNDS.ENCIMA);
      }}
      data-card-meta="true"
    >
      <div
        ref={circleRef}
        className={clsx(styles.circle, disabled && styles.disabledCircle)}
        data-circle="true"
      />
      <div className={styles.container}>{children}</div>
    </motion.button>
  );
}
